import { useCallback, useMemo, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { Buttons } from 'components';
import { routes } from 'config';
import NetworkCampaigns from './components/networkCampaigns/NetworkCampaigns';
import DirectBuyCampaigns from './components/directBuyCampaigns/DirectBuyCampaigns';
import {
  showPolipop,
  convertNumbersToStrings,
  convertCountries,
  getErrorFieldName,
  getNetworkAndDirectBuyNames,
} from 'utils';
import { minServingFields } from 'constants';
import { manageAppsAPI } from 'services/manageApps.service';
import { validateCountries } from './validation';
import * as S from './MinimumServingForm.styled';

let timer_id;

const { networkField, networkCountriesError, directBuyField, directBuyCountriesError } =
  getNetworkAndDirectBuyNames(minServingFields);

function replaceZeros(arr) {
  if (!arr) return [];

  return arr.map((item) => {
    const formatedItem = { ...item };
    Object.keys(item).forEach((key) => {
      if (item[key] == 0) formatedItem[key] = '';
    });
    return formatedItem;
  });
}

function getDefaultValue(data, networkField, directBuyField) {
  return {
    [networkField]: {
      minCpi: data?.defaultAppMinEcpiBidBudget[0]?.minCpi || '',
      minEcpm: data?.defaultAppMinEcpiBidBudget[0]?.minEcpm || '',
      countries: replaceZeros(data?.servingModel),
    },
    [directBuyField]: {
      minCpi: data?.defaultAppMinEcpiBidBudget[0]?.directMinCpi || '',
      countries: replaceZeros(data?.directBuymodel),
    },
  };
}

const MinimumServingForm = ({
  data,
  result = {
    status: 'uninitialized',
    isUninitialized: true,
    isLoading: false,
    isSuccess: false,
    isError: false,
  },
  updateAction = () => console.log('pass save action'),
  isFetching,
}) => {
  const navigate = useNavigate();
  const { pba_id } = useParams();
  const { data: all_countries } = manageAppsAPI.useGetCountriesListQuery();
  const [redirect, setRedirect] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValue = useMemo(() => getDefaultValue(data, networkField, directBuyField), [data]);
  const manageAppsLink = routes.list.find((route) => route.id === 'manageApps').url;

  const {
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { isDirty, errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const networkCampaignsCountries = useWatch({
    name: `${networkField}.countries`,
    control,
  });

  const directBuyCampaignsCountries = useWatch({
    name: `${directBuyField}.countries`,
    control,
  });

  const isFormValid = isValid && !errors[networkCountriesError] && !errors[directBuyCountriesError];
  const isSaveDisabled = !isDirty || !isFormValid || isFetching || isSubmitting;

  const onSubmit = async (formData) => {
    if (!isValid) return;
    const isValidCountries = validateCountries(formData, setError);
    if (!isValidCountries) return;

    setIsSubmitting(true);

    // if the user deletes a country that was initially fetched from the database, then when the form is submitted,
    // that country should still be sent to the server, but with an empty value for the directCpi field.
    const newData = {
      [directBuyField]: {
        ...formData[directBuyField],
        countries: convertCountries(data.directBuymodel, formData[directBuyField].countries),
      },
      [networkField]: {
        ...formData[networkField],
        countries: convertCountries(data.servingModel, formData[networkField].countries),
      },
      pba_id,
    };

    const res = await updateAction(convertNumbersToStrings(newData));
    setIsSubmitting(false);
    return res;
  };

  const handleClearCountries = useCallback((field) => {
    if (field !== networkField && field !== directBuyField) return;

    setValue(`${field}.countries`, [], { shouldDirty: true });
    clearErrors(getErrorFieldName(field, minServingFields));
    trigger(`${field}.countries`);
  }, []);

  const handleDeleteCountry = useCallback(
    (field, cca2) => {
      let countries = [];
      if (field === networkField) countries = networkCampaignsCountries;
      if (field === directBuyField) countries = directBuyCampaignsCountries;

      const newValues = countries.filter((c) => c.cca2 !== cca2);
      setValue(`${field}.countries`, newValues, { shouldDirty: true });
      trigger(`${field}.countries`);
    },
    [networkCampaignsCountries, directBuyCampaignsCountries],
  );

  const saveButtonDirectBuyCampaigns = useCallback(
    (data) => {
      const addData = Object.values(all_countries)
        .flat()
        .filter((item) => data.includes(item.cca2))
        .map((data) => {
          const containedItem = directBuyCampaignsCountries.find(({ cca2 }) => cca2 === data.cca2);
          if (containedItem) return containedItem;
          return { ...data, directCpi: '' };
        });
      setValue(`${directBuyField}.countries`, addData, { shouldDirty: true });
    },
    [all_countries, directBuyCampaignsCountries],
  );

  const saveButtonNetworkCampaigns = useCallback(
    (data) => {
      const addData = Object.values(all_countries)
        .flat()
        .filter((item) => data.includes(item.cca2))
        .map((data) => {
          const containedItem = networkCampaignsCountries.find(({ cca2 }) => cca2 === data.cca2);
          if (containedItem) return containedItem;
          return { ...data, minCpi: '', minEcpm: '' };
        });
      setValue(`${networkField}.countries`, addData, { shouldDirty: true });
    },
    [all_countries, networkCampaignsCountries],
  );

  const handleSaveAndClose = () => {
    handleSubmit();
    setRedirect(true);
  };

  useEffect(() => {
    if (!isFetching && data) {
      reset(defaultValue);
      // validate data from server, setTimeout needed as reset is async
      setTimeout(() => trigger(), 0);
    }
  }, [isFetching]);

  useEffect(() => {
    showPolipop(
      result,
      () => {},
      () => reset(watch()),
    );

    if (redirect && result.isSuccess) {
      timer_id = setTimeout(() => {
        navigate(manageAppsLink);
        reset(watch());
      }, 2500);
    }
    return () => {
      clearTimeout(timer_id);
    };
  }, [result]);

  return (
    <S.Container>
      <S.Form onSubmit={handleSubmit(onSubmit)} id='minServing'>
        <S.Box>
          <NetworkCampaigns
            defaultAppMinEcpiBidBudget={data?.defaultAppMinEcpiBidBudget ?? []}
            control={control}
            setValue={setValue}
            networkCampaignsCountries={networkCampaignsCountries}
            handleClearCountries={handleClearCountries}
            saveButtonNetworkCampaigns={saveButtonNetworkCampaigns}
            clearErrors={clearErrors}
            errors={errors}
            handleDeleteCountry={handleDeleteCountry}
          />
        </S.Box>
        <S.Box>
          <DirectBuyCampaigns
            appExclusionsDetails={data?.app_exclusions_details ?? []}
            control={control}
            setValue={setValue}
            directBuyCampaignsCountries={directBuyCampaignsCountries}
            handleClearCountries={handleClearCountries}
            errors={errors}
            clearErrors={clearErrors}
            saveButtonDirectBuyCampaigns={saveButtonDirectBuyCampaigns}
            handleDeleteCountry={handleDeleteCountry}
          />
        </S.Box>
        <S.BtnGroup>
          <Buttons.PrimaryNormal
            type='submit'
            isDisabled={isSaveDisabled}
            id='saveButton'
            onClick={handleSubmit}
          >
            Save Settings
          </Buttons.PrimaryNormal>
          <Buttons.SecondaryNormal
            type='submit'
            isDisabled={isSaveDisabled}
            id='saveAndCloseButton'
            onClick={handleSaveAndClose}
          >
            Save & Close
          </Buttons.SecondaryNormal>
          <Buttons.SecondaryNormal id='cancelButton' onClick={() => navigate(manageAppsLink)}>
            Cancel
          </Buttons.SecondaryNormal>
        </S.BtnGroup>
      </S.Form>
    </S.Container>
  );
};

export default MinimumServingForm;
